import { promotionKindLabels, type Promotion } from '../api';
import { BonusCodeButton } from '@/components/ui/BonusCodeButton';

/**
 * One promotion, as it appears on the home page and the promotions list.
 *
 * The site's name, icon and accent colour come from this module's own copy of
 * them — kept current by the catalog's events — so a card renders without
 * reaching across a module boundary.
 */
export function PromotionCard({ promotion }: { promotion: Promotion }) {
  const kind = promotionKindLabels[promotion.kind] ?? promotionKindLabels.other;

  return (
    <article
      data-tilt
      className="promotion-card relative flex flex-col gap-4 overflow-hidden rounded-[var(--radius-card)] border border-surface-800 bg-surface-900 p-6"
    >
      <span className="card-light" aria-hidden="true" />
      {/*
        The owner's artwork behind every card.

        A background rather than an <img>: it is decoration, and a background
        cannot be dragged out of the card or announced to a screen reader.

        Anchored bottom-right, where the gift boxes and dice sit — the top-left
        of the picture is near-black, and that is where the site name and the
        promotion title land. Scrimmed hard on top of that, because the text
        over it has to stay readable no matter which promotion's colour wash
        lands on the same pixels.
      */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-cover bg-right-bottom bg-no-repeat opacity-40"
        style={{ backgroundImage: 'url(/promotion-bg.webp)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-surface-900 via-surface-900/90 to-surface-900/55"
        // The site's colour as a soft wash on top, so a row of cards is still
        // scannable by brand. Inline because the value comes from the database.
        style={{
          backgroundImage: `radial-gradient(120% 80% at 100% 0%, ${promotion.siteBrandColor}22, transparent 60%)`,
        }}
      />

      <div className="relative flex flex-1 flex-col gap-5">
        <header className="flex items-center gap-3">
          {promotion.siteIconUrl ? (
            <img
              src={promotion.siteIconUrl}
              alt=""
              width={40}
              height={40}
              loading="lazy"
              className="size-10 rounded-lg object-cover"
              style={{ boxShadow: `0 0 0 1px ${promotion.siteBrandColor}66` }}
            />
          ) : (
            <span className="grid size-10 place-items-center rounded-lg bg-surface-800 font-bold text-surface-400">
              {promotion.siteName.slice(0, 1)}
            </span>
          )}

          <span className="min-w-0 flex-1">
            <span className="block truncate font-semibold text-surface-50">
              {promotion.siteName}
            </span>
            <span className="block text-xs text-surface-500">{kind}</span>
          </span>
        </header>

        <div className="min-w-0">
          <h3 className="text-lg font-extrabold uppercase leading-tight tracking-tight text-surface-50">
            {promotion.title}
          </h3>
          {promotion.subtitle ? (
            <p className="mt-1 text-sm text-brand-400">{promotion.subtitle}</p>
          ) : null}
        </div>

        {promotion.code ? <BonusCodeButton code={promotion.code} /> : null}

        <a
          href={promotion.clickUrl}
          target="_blank"
          // sponsored tells a crawler this is a paid link, which is what it is.
          rel="noreferrer noopener sponsored"
          className="gf-button gf-button-primary mt-auto"
        >
          Bonusu al
          <svg aria-hidden viewBox="0 0 24 24" className="size-4 fill-current">
            <path d="M13 5l7 7-7 7v-4H4v-6h9V5z" />
          </svg>
        </a>

        {promotion.terms ? (
          <details className="group">
            <summary className="cursor-pointer list-none text-sm text-surface-500 transition hover:text-surface-300">
              Şartlar ve koşullar
              <span aria-hidden className="ml-1 inline-block transition group-open:rotate-90">
                ›
              </span>
            </summary>
            <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-surface-500">
              {promotion.terms}
            </p>
          </details>
        ) : null}
      </div>
    </article>
  );
}
